import { Renderer, Program, Mesh, Color, Triangle } from "ogl";
import { useEffect, useRef } from "react";
import './Aurora.css';

const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
uniform vec2 uResolution;
uniform float uBlend;

out vec4 fragColor;

vec3 permute(vec3 x) {
  return mod(((x * 34.0) + 1.0) * x, 289.0);
}

float snoise(vec2 v){
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  
  float t = uv.x;
  vec3 color;
  
  if (t < 0.5) {
    color = mix(uColor1, uColor2, t * 2.0);
  } else {
    color = mix(uColor2, uColor3, (t - 0.5) * 2.0);
  }
  
  float height = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  height = exp(height);
  height = (uv.y * 2.0 - height + 0.2);
  float intensity = 0.6 * height;
  
  float midPoint = 0.20;
  float auroraAlpha = smoothstep(midPoint - uBlend * 0.5, midPoint + uBlend * 0.5, intensity);
  
  vec3 auroraColor = intensity * color;
  
  fragColor = vec4(auroraColor * auroraAlpha, auroraAlpha);
}`;

export default function Aurora({ 
  colorStops = ["#00d8ff", "#7cff67", "#00d8ff"],
  amplitude = 1.0,
  blend = 0.5,
  speed = 1.0
}) {
  const propsRef = useRef({ colorStops, amplitude, blend, speed });
  propsRef.current = { colorStops, amplitude, blend, speed };

  const ctnDom = useRef(null);
  const rendererRef = useRef(null);
  const meshRef = useRef(null);
  const programRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const ctn = ctnDom.current;
    if (!ctn) return;

    try {
      const width = Math.max(ctn.offsetWidth, 1);
      const height = Math.max(ctn.offsetHeight, 1);
      
      const renderer = new Renderer({
        alpha: true,
        premultipliedAlpha: true,
        antialias: true,
        width,
        height,
        dpr: window.devicePixelRatio
      });
      rendererRef.current = renderer;

      const gl = renderer.gl;
      if (!gl) {
        console.error('Failed to get WebGL context');
        return;
      }

      gl.clearColor(0, 0, 0, 0);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.canvas.style.backgroundColor = 'transparent';
      gl.canvas.style.width = '100%';
      gl.canvas.style.height = '100%';

      const geometry = new Triangle(gl);
      const vertices = new Float32Array([-1, -1, 3, -1, -1, 3]);
      geometry.addAttribute('position', { size: 2, data: vertices });

      const [color1, color2, color3] = colorStops.map(hex => new Color(hex));

      const program = new Program(gl, {
        vertex: VERT,
        fragment: FRAG,
        uniforms: {
          uTime: { value: 0 },
          uAmplitude: { value: amplitude },
          uColor1: { value: [color1.r, color1.g, color1.b] },
          uColor2: { value: [color2.r, color2.g, color2.b] },
          uColor3: { value: [color3.r, color3.g, color3.b] },
          uResolution: { value: [width, height] },
          uBlend: { value: blend }
        }
      });
      programRef.current = program;

      const mesh = new Mesh(gl, { geometry, program });
      meshRef.current = mesh;

      ctn.appendChild(gl.canvas);

      const handleResize = () => {
        if (!ctn || !program || !renderer) return;
        const newWidth = Math.max(ctn.offsetWidth, 1);
        const newHeight = Math.max(ctn.offsetHeight, 1);
        renderer.setSize(newWidth, newHeight);
        program.uniforms.uResolution.value = [newWidth, newHeight];
      };

      window.addEventListener("resize", handleResize);

      let startTime = performance.now();
      const update = () => {
        if (!mesh || !program || !renderer) return;
        
        const currentTime = performance.now();
        const elapsed = (currentTime - startTime) * 0.001;
        
        program.uniforms.uTime.value = elapsed * propsRef.current.speed;
        program.uniforms.uAmplitude.value = propsRef.current.amplitude;
        program.uniforms.uBlend.value = propsRef.current.blend;
        
        const [c1, c2, c3] = propsRef.current.colorStops.map(hex => new Color(hex));
        program.uniforms.uColor1.value = [c1.r, c1.g, c1.b];
        program.uniforms.uColor2.value = [c2.r, c2.g, c2.b];
        program.uniforms.uColor3.value = [c3.r, c3.g, c3.b];
        
        gl.clear(gl.COLOR_BUFFER_BIT);
        renderer.render({ scene: mesh });
        animationFrameRef.current = requestAnimationFrame(update);
      };

      animationFrameRef.current = requestAnimationFrame(update);

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        window.removeEventListener("resize", handleResize);
        
        if (meshRef.current) {
          meshRef.current.geometry?.remove();
          meshRef.current.program?.remove();
        }
        
        if (ctn && gl.canvas.parentNode === ctn) {
          ctn.removeChild(gl.canvas);
        }
        
        gl.getExtension("WEBGL_lose_context")?.loseContext();
        
        rendererRef.current = null;
        meshRef.current = null;
        programRef.current = null;
      };
    } catch (error) {
      console.error('Error initializing Aurora:', error);
    }
  }, [colorStops, amplitude, blend, speed]);

  return (
    <div 
      ref={ctnDom} 
      className="aurora-container" 
      style={{ 
        width: '100%', 
        height: '25vh',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        maxHeight: '25vh'
      }} 
    />
  );
} 